import { twicpicsUrl } from "src/config";

export const isImage = (fileName: string) => {
  const fileFormat = fileName?.split(".")[1];

  return ["jpg", "jpeg", "png"]?.includes(fileFormat?.toLowerCase());
};

export const getImagePathFromImgixUrl = (imgixUrl: string): string => {
  try {
    const url = new URL(imgixUrl);
    return url.pathname;
  } catch {
    return imgixUrl;
  }
};

export interface ImageUrlResult {
  url: string;
  isTwicpics: boolean;
  // True only when an imgix URL was rewritten to a twicpics path because
  // imgix is decommissioned and twicpicsUrl is configured.
  isImgixFallback: boolean;
}

const hasHostname = (url: string, hostname: string): boolean => {
  try {
    const parsed = new URL(url);
    const parsedHostname = parsed.hostname.toLowerCase();
    return (
      parsed.protocol === "https:" &&
      (parsedHostname === hostname || parsedHostname.endsWith(`.${hostname}`))
    );
  } catch {
    return false;
  }
};

const isTwicpicsUrl = (url: string): boolean => hasHostname(url, "twic.pics");

const isImgixUrl = (url: string): boolean => hasHostname(url, "imgix.net");

const hasSameOrigin = (firstUrl: string, secondUrl: string): boolean => {
  try {
    return new URL(firstUrl).origin === new URL(secondUrl).origin;
  } catch {
    return false;
  }
};

export const resolveImageUrl = (imageUrl: string): ImageUrlResult => {
  if (!imageUrl) return { url: "", isTwicpics: false, isImgixFallback: false };
  if (isTwicpicsUrl(imageUrl)) {
    // Only strip to a bare path when a TwicPics domain is actually installed
    // and shares this origin. src/index.tsx skips installTwicpics when the
    // variable is empty, so a bare path would render nothing.
    if (!twicpicsUrl || !hasSameOrigin(imageUrl, twicpicsUrl)) {
      return { url: imageUrl, isTwicpics: false, isImgixFallback: false };
    }
    return {
      url: getImagePathFromImgixUrl(imageUrl),
      isTwicpics: true,
      isImgixFallback: false,
    };
  }
  if (twicpicsUrl && isImgixUrl(imageUrl)) {
    return {
      url: getImagePathFromImgixUrl(imageUrl),
      isTwicpics: true,
      isImgixFallback: true,
    };
  }
  return { url: imageUrl, isTwicpics: false, isImgixFallback: false };
};

export const buildFullImageUrl = (imageUrl: string): string => {
  const resolved = resolveImageUrl(imageUrl);
  if (resolved.isTwicpics) {
    return `${twicpicsUrl.replace(/\/$/, "")}${resolved.url}`;
  }
  return resolved.url;
};

/**
 * Append a TwicPics transform to an image the API serves.
 *
 * The decision is made on the source host, not on `resolveImageUrl`'s
 * origin-gated `isTwicpics` flag. The API hardcodes its stills to
 * `contact-media.twic.pics`, and they exist only there, while the staging and
 * production bundles configure a different TwicPics origin — so that flag is
 * false in every deployed environment, and gating the transform on it ships
 * the untransformed originals. Any TwicPics host keeps the host it came with
 * and gets the transform; a decommissioned imgix source is repointed onto the
 * configured TwicPics origin; anything else is returned untouched, because
 * TwicPics parameters mean nothing to S3 or Contentful.
 *
 * `transform` is the part after `twic=v1/`, e.g. `cover=600x800/output=webp`.
 * URLSearchParams is deliberately not used: it percent-encodes the slashes.
 */
export const applyTwicpicsTransform = (
  imageUrl: string,
  transform: string,
): string => {
  if (!imageUrl) return "";
  if (isTwicpicsUrl(imageUrl)) {
    const separator = imageUrl.includes("?") ? "&" : "?";
    return `${imageUrl}${separator}twic=v1/${transform}`;
  }
  const resolved = resolveImageUrl(imageUrl);
  if (resolved.isImgixFallback) {
    const origin = twicpicsUrl.replace(/\/$/, "");
    return `${origin}${resolved.url}?twic=v1/${transform}`;
  }
  return imageUrl;
};

/**
 * Keep the legacy fixed-height behaviour for non-TwicPics sources while
 * routing API-owned imgix URLs through the configured TwicPics origin.
 */
export const buildFixedHeightImageUrl = (
  imageUrl: string,
  height: number,
): string => {
  const transformedUrl = applyTwicpicsTransform(imageUrl, `resize=-x${height}`);

  if (transformedUrl !== imageUrl) return transformedUrl;

  try {
    const url = new URL(imageUrl);
    url.searchParams.set("h", String(height));
    return url.toString();
  } catch {
    return imageUrl;
  }
};
